import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from "../components/layout/Sidebar";
import Navbar from "../components/layout/Navbar";
import ScrollToTop from "../components/layout/ScrollToTop";
// TEMPORARY (redesign launch week) — remove with src/components/WelcomeIntro.jsx
import WelcomeIntro from "../components/WelcomeIntro";
import AssistantWidget from "../components/assistant/AssistantWidget";
import NotificationPrompt from "../components/notifications/NotificationPrompt";
import PushNotification from "../components/notifications/PushNotification";
import useFirebaseNotifications from "../hooks/useFirebaseNotifications";
import useAuth from "../hooks/useAuth";
import useRequestNotifications from "../hooks/useRequestNotifications";
import { useAttendanceSocket } from "../hooks/useAttendanceSocket";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  DevRulesLockProvider,
  useDevRulesLock,
} from "../context/DevRulesLockProvider";

const DevRulesLockOverlay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lockState } = useDevRulesLock();

  const isLocked = Boolean(lockState?.locked);
  const onRulesPage = location.pathname === "/main/dev-rules";

  if (!isLocked || onRulesPage) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120] bg-gray-100/95 dark:bg-primary-bg/95 backdrop-blur-sm px-4">
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-full max-w-xl rounded-2xl border border-gray-200 dark:border-dark-100 bg-white dark:bg-primary-card shadow-2xl p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 w-14 h-14 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-primary-accent" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-light-100 mb-2">
            New Rules
          </h2>
          <p className="text-gray-600 dark:text-light-400 mb-6">
            There are new Dev Team rules that you must review
          </p>
          <button
            onClick={() => navigate("/main/dev-rules")}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary-accent to-orange-600 text-white font-medium px-5 py-2.5 hover:shadow-lg"
          >
            View Rules
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const REQUEST_TYPE_LABELS = {
  vacation: "Vacation",
  mission: "Mission",
  late_coming: "Late coming",
  early_leave: "Early leave",
};

const formatShortDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const resolveEmployeeTeam = (employee) => {
  if (Array.isArray(employee?.teams) && employee.teams.length > 0) {
    const highPotitEntry = employee.teams.find((entry) => {
      if (typeof entry === "string") {
        return entry === "high_potit";
      }

      return entry?.team === "high_potit";
    });

    if (highPotitEntry) {
      return "high_potit";
    }

    const firstTeamEntry = employee.teams.find((entry) => {
      if (typeof entry === "string") {
        return Boolean(entry);
      }

      return Boolean(entry?.team);
    });

    if (firstTeamEntry) {
      return typeof firstTeamEntry === "string"
        ? firstTeamEntry
        : firstTeamEntry.team;
    }
  }

  return employee?.team || null;
};

const Layout = () => {
  const { auth } = useAuth();
  // Support both /me response (_id) and login response (id); normalize to string for consistency
  const rawId = auth?.employee?._id ?? auth?.employee?.id;
  const userId = rawId != null ? String(rawId) : null;
  const token =
    auth?.accessToken ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accessToken")
      : null);
  const team = resolveEmployeeTeam(auth?.employee);

  // Attendance: auto check-in on login, heartbeat every 2 minutes
  useAttendanceSocket({ token, enabled: !!token });

  const isMobile = useMediaQuery("(max-width: 767px)");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isImmersive, setIsImmersive] = useState(false);

  // Stack of push notifications (multiple can show at once, stacked vertically)
  const [pushNotifications, setPushNotifications] = useState([]);

  const removePushNotification = (id) => {
    setPushNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const addPushNotification = useCallback(({ title, body, data = {} }) => {
    const id = `push-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setPushNotifications((prev) => [
      ...prev,
      { id, title: title || "Notification", body: body || "", data },
    ]);
  }, []);

  // Real-time request notifications: High Potit (new/edit), all users (status update)
  const handleRequestNotify = useCallback(
    ({ type, request, status }) => {
      const emp = request?.employee;
      const name = emp?.fullNameEn || emp?.fullNameAr || "An employee";
      const typeLabel =
        REQUEST_TYPE_LABELS[request?.requestType] ||
        request?.requestType ||
        "Request";
      const start = formatShortDate(request?.startDate);
      const end = formatShortDate(request?.endDate);
      const range = start && end ? ` (${start} – ${end})` : "";

      if (type === "request:new") {
        addPushNotification({
          title: "New request",
          body: `${name} submitted a ${typeLabel} request${range}.`,
          data: {
            type: "request_new",
            requestId: request?._id,
            url: "/week-schedule/requests",
          },
        });
      } else if (type === "request:edit_requested") {
        addPushNotification({
          title: "Request edit for confirmation",
          body: `${name} sent an edit for a ${typeLabel} request${range} for your approval.`,
          data: {
            type: "request_edit_requested",
            requestId: request?._id,
            url: "/week-schedule/requests",
          },
        });
      } else if (type === "request:status_updated" && status) {
        const isApproved = status === "approved";
        addPushNotification({
          title: isApproved ? "Request approved" : "Request rejected",
          body: `Your ${typeLabel} request${range} was ${isApproved ? "approved" : "rejected"}.`,
          data: {
            type: "request_status_updated",
            requestId: request?._id,
            status,
            url: "/week-schedule/requests",
          },
        });
      } else if (type === "request:updated") {
        addPushNotification({
          title: "Request updated",
          body: `Your ${typeLabel} request${range} was updated by admin.`,
          data: {
            type: "request_updated",
            requestId: request?._id,
            url: "/week-schedule/requests",
          },
        });
      }
    },
    [addPushNotification],
  );

  useRequestNotifications({
    token,
    team,
    onNotify: handleRequestNotify,
  });

  // Initialize Firebase notifications
  const { initializeNotifications } = useFirebaseNotifications(userId);

  // Show push notification when received in the foreground – append to stack instead of replace
  useEffect(() => {
    const handleForegroundNotification = (e) => {
      const { title, body, data } = e.detail || {};
      if (title || body) {
        addPushNotification({
          title: title || "New Notification",
          body: body || "",
          data: data || {},
        });
      }
    };
    window.addEventListener(
      "firebase-foreground-notification",
      handleForegroundNotification,
    );
    return () =>
      window.removeEventListener(
        "firebase-foreground-notification",
        handleForegroundNotification,
      );
  }, [addPushNotification]);

  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener("sidebarCollapse", handleSidebarCollapse);
    return () =>
      window.removeEventListener("sidebarCollapse", handleSidebarCollapse);
  }, []);

  // On mobile: toggleSidebar opens/closes drawer; closeMobileMenu closes it
  useEffect(() => {
    const handleToggleSidebar = () => {
      if (isMobile) setMobileMenuOpen((prev) => !prev);
    };
    const handleCloseMobileMenu = () => setMobileMenuOpen(false);
    window.addEventListener("toggleSidebar", handleToggleSidebar);
    window.addEventListener("closeMobileMenu", handleCloseMobileMenu);
    return () => {
      window.removeEventListener("toggleSidebar", handleToggleSidebar);
      window.removeEventListener("closeMobileMenu", handleCloseMobileMenu);
    };
  }, [isMobile]);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  // Immersive reader mode (article view hides sidebar + navbar)
  useEffect(() => {
    const enter = () => setIsImmersive(true);
    const exit = () => setIsImmersive(false);
    window.addEventListener("enterImmersiveMode", enter);
    window.addEventListener("exitImmersiveMode", exit);
    return () => {
      window.removeEventListener("enterImmersiveMode", enter);
      window.removeEventListener("exitImmersiveMode", exit);
    };
  }, []);

  // Initialize notifications when user is logged in
  useEffect(() => {
    if (userId) {
      // Add a small delay to ensure everything is ready
      const timer = setTimeout(() => {
        initializeNotifications();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [userId, initializeNotifications]);

  return (
    <DevRulesLockProvider enabled={!!token}>
      <div className="min-h-screen transition-colors duration-200 [overflow-x:clip]">
        {/* Scroll to top on route change */}
        <ScrollToTop />

        {/* Sidebar: drawer on mobile, fixed sidebar on desktop */}
        <Sidebar
          isMobile={isMobile}
          mobileMenuOpen={mobileMenuOpen}
          onCloseMobileMenu={() => setMobileMenuOpen(false)}
          isImmersive={isImmersive}
        />

        {/* Main Content Area: full width on mobile, offset on desktop */}
        <div
          className="min-h-screen transition-all duration-[400ms]"
          style={{
            marginLeft: isImmersive
              ? 0
              : isMobile
                ? 0
                : sidebarCollapsed
                  ? `${SIDEBAR_COLLAPSED_WIDTH}px`
                  : `${SIDEBAR_WIDTH}px`,
          }}
        >
          {/* Navbar */}
          <Navbar isMobile={isMobile} isImmersive={isImmersive} />

          {/* Page Content: reduced padding on mobile */}
          <main
            className="pt-5 md:pt-7 px-3 sm:px-4 md:px-7 pb-8 [overflow-x:clip] transition-all duration-[400ms]"
            style={{ marginTop: isImmersive ? "-4rem" : undefined, paddingTop: isImmersive ? "2.5rem" : undefined }}
          >
            <Outlet />
          </main>
        </div>

        {/* Notification Prompt */}
        <NotificationPrompt />
        {/* Stacked push notifications: responsive position/size */}
        <div className="fixed top-14 left-3 right-3 sm:left-auto sm:right-4 sm:min-w-[320px] sm:max-w-[420px] z-[9999] flex flex-col gap-3 pointer-events-none">
          <div className="flex flex-col gap-3 pointer-events-auto">
            {pushNotifications.map((notification) => (
              <PushNotification
                key={notification.id}
                title={notification.title}
                body={notification.body}
                isVisible={true}
                onClose={() => removePushNotification(notification.id)}
                data={notification.data}
                duration={5000}
                stacked={true}
              />
            ))}
          </div>
        </div>
        <AssistantWidget />
        <DevRulesLockOverlay />
        {/* TEMPORARY: one-time redesign welcome intro */}
        <WelcomeIntro />
      </div>
    </DevRulesLockProvider>
  );
};

export default Layout;
